import { useState, useEffect, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { lessonsAPI, meditationAPI } from "../services/api";
import { formatTime } from "../utils/helpers";
import { useMeditationAudio, useAmbientAudio } from "../hooks/useAudio";
import { PageWrapper, Card, Button, Textarea, MeditationPlayer, AmbientSoundMixer, FullScreenGlow } from "../components";
import { ArrowLeft, Clock, Play, Check, Copy } from "lucide-react";

export const LessonPage = () => {
  const { lessonId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const programId = searchParams.get("program");
  const moduleId = searchParams.get("module");

  const [lesson, setLesson] = useState(null);
  const [reflections, setReflections] = useState({});
  const [completedSteps, setCompletedSteps] = useState({});
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [shareText, setShareText] = useState("");
  const [showShare, setShowShare] = useState(false);
  const [timerActive, setTimerActive] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [defaultsApplied, setDefaultsApplied] = useState(false);
  const [showCompletionGlow, setShowCompletionGlow] = useState(false);
  
  // Meditation audio state
  const [meditationAudio, setMeditationAudio] = useState(null);
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [ambientSounds, setAmbientSounds] = useState({});

  // Use audio hooks
  const {
    audioRef,
    audioPlaying,
    audioProgress,
    voiceVolume,
    setVoiceVolume,
    setInitialVolume: setInitialVoiceVolume,
    togglePlayback: toggleMeditationPlayback,
  } = useMeditationAudio();

  const {
    ambientRef,
    activeAmbient,
    ambientVolume,
    setAmbientVolume,
    setInitialVolume: setInitialAmbientVolume,
    ambientPlaying,
    selectSound: selectAmbientSound,
    togglePlayback: toggleAmbientPlayback,
    setDefaultSound,
  } = useAmbientAudio();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [lessonRes, reflectionsRes, audioRes, ambientRes] = await Promise.all([
          lessonsAPI.getById(lessonId),
          lessonsAPI.getReflections(lessonId),
          meditationAPI.getAudio(lessonId).catch(() => ({ data: null })),
          meditationAPI.getAmbientSounds().catch(() => ({ data: { sounds: {} } }))
        ]);
        const lessonData = lessonRes.data;
        setLesson(lessonData);
        setMeditationAudio(audioRes.data);
        setAmbientSounds(ambientRes.data?.sounds || {});
        
        const refMap = {};
        reflectionsRes.data.forEach(r => {
          refMap[r.prompt] = r.response;
        });
        setReflections(refMap);

        // Apply lesson defaults for volume and ambient sound
        if (lessonData.default_voice_volume !== undefined) {
          setInitialVoiceVolume(lessonData.default_voice_volume);
        }
        if (lessonData.default_ambient_volume !== undefined) {
          setInitialAmbientVolume(lessonData.default_ambient_volume);
        }
        // Pre-select default ambient sound (without auto-playing)
        if (lessonData.default_ambient && lessonData.default_ambient !== "none") {
          const sounds = ambientRes.data?.sounds || {};
          if (sounds[lessonData.default_ambient]) {
            setDefaultSound(lessonData.default_ambient, sounds[lessonData.default_ambient].url, false);
          }
        }
        setDefaultsApplied(true);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [lessonId, setInitialVoiceVolume, setInitialAmbientVolume, setDefaultSound]);

  useEffect(() => {
    let interval;
    if (timerActive) {
      interval = setInterval(() => {
        setTimerSeconds(s => s + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timerActive]);

  // Update audio ref when meditation audio changes
  useEffect(() => {
    if (audioRef.current && meditationAudio?.audio_url) {
      audioRef.current.src = meditationAudio.audio_url;
    }
  }, [meditationAudio, audioRef]);

  const handleReflectionChange = (prompt, value) => {
    setReflections(prev => ({ ...prev, [prompt]: value }));
  };

  const handleReflectionBlur = async (prompt) => {
    if (reflections[prompt]) {
      try {
        await lessonsAPI.saveReflection(lessonId, prompt, reflections[prompt]);
      } catch (e) {
        console.error(e);
      }
    }
  };

  const handleComplete = async () => {
    setCompleting(true);
    try {
      const res = await lessonsAPI.completeLesson(lessonId, programId, moduleId);
      
      // Trigger completion glow
      setShowCompletionGlow(true);
      setTimeout(() => setShowCompletionGlow(false), 1500);
      
      setShareText(res.data.share_text);
      setShowShare(true);
    } catch (e) {
      console.error(e);
    } finally {
      setCompleting(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareText);
  };

  const generateMeditationAudio = async () => {
    setGeneratingAudio(true);
    try {
      const res = await meditationAPI.generateAudio(lessonId);
      setMeditationAudio(res.data);
    } catch (e) {
      console.error(e);
      const errorDetail = e.response?.data?.detail || "Failed to generate meditation audio.";
      if (e.response?.status === 402) {
        alert("ElevenLabs free tier limit reached. A paid ElevenLabs subscription is required for AI meditation audio generation.");
      } else {
        alert(errorDetail);
      }
    } finally {
      setGeneratingAudio(false);
    }
  };

  if (loading) {
    return (
      <PageWrapper showNav={false}>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-pulse-gentle text-[#4A6741]">Loading...</div>
        </div>
      </PageWrapper>
    );
  }

  if (!lesson) return null;

  const allStepsCompleted = lesson.practice_steps.every((_, i) => completedSteps[i]);
  const allReflectionsFilled = lesson.reflection_prompts.every(p => reflections[p]?.trim());

  return (
    <PageWrapper showNav={false}>
      <div className="min-h-screen">
        {/* Header */}
        <div className="sticky top-0 glass border-b border-[#E6E2DD] z-40">
          <div className="flex items-center gap-3 p-4 max-w-2xl mx-auto">
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 rounded-full bg-[#F0EFEB] flex items-center justify-center"
              data-testid="back-btn"
            >
              <ArrowLeft size={20} className="text-[#292524]" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-lg text-[#292524] truncate">{lesson.title}</h1>
              <p className="text-sm text-[#78716C]">{lesson.estimated_minutes} min</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-8 max-w-2xl mx-auto pb-32">
          {/* Hidden audio elements */}
          <audio ref={audioRef} preload="auto" />
          <audio ref={ambientRef} loop preload="auto" />

          {/* 1. Opening Intention */}
          <section className="space-y-3 animate-fade-in-up">
            <p className="text-xs uppercase tracking-wide text-[#D4A373]">Opening Intention</p>
            <p className="font-heading text-xl text-[#292524] italic leading-relaxed">
              "{lesson.intention_text}"
            </p>
          </section>

          {/* Guided Meditation Audio - only show if TTS enabled */}
          {(lesson.tts_enabled !== false) && (
            <MeditationPlayer
              meditationAudio={meditationAudio}
              audioRef={audioRef}
              audioPlaying={audioPlaying}
              audioProgress={audioProgress}
              voiceVolume={voiceVolume}
              setVoiceVolume={setVoiceVolume}
              onTogglePlayback={toggleMeditationPlayback}
              onGenerate={generateMeditationAudio}
              generatingAudio={generatingAudio}
            />
          )}

          {/* Ambient Sounds Mixer */}
          <AmbientSoundMixer
            ambientSounds={ambientSounds}
            ambientRef={ambientRef}
            activeAmbient={activeAmbient}
            ambientVolume={ambientVolume}
            setAmbientVolume={setAmbientVolume}
            ambientPlaying={ambientPlaying}
            onSelectSound={selectAmbientSound}
            onTogglePlayback={toggleAmbientPlayback}
          />

          {/* 2. Video */}
          <section className="space-y-3 animate-fade-in-up stagger-1">
            <p className="text-xs uppercase tracking-wide text-[#78716C]">Video</p>
            {lesson.video_url ? (
              <div className="aspect-video rounded-2xl overflow-hidden bg-[#292524]">
                <iframe
                  src={lesson.video_url}
                  className="w-full h-full"
                  allowFullScreen
                  title="Lesson video"
                />
              </div>
            ) : (
              <div className="aspect-video rounded-2xl bg-[#E6E2DD] flex items-center justify-center">
                <div className="text-center text-[#78716C]">
                  <Play size={48} className="mx-auto mb-2 opacity-50" />
                  <p>Video coming soon</p>
                </div>
              </div>
            )}
          </section>

          {/* 3. Practice */}
          <section className="space-y-4 animate-fade-in-up stagger-2">
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-wide text-[#78716C]">Practice</p>
              <button
                onClick={() => {
                  setTimerActive(!timerActive);
                  if (!timerActive) setTimerSeconds(0);
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors
                  ${timerActive ? "bg-[#D4A373] text-white" : "bg-[#E6E2DD] text-[#292524]"}`}
                data-testid="timer-btn"
              >
                <Clock size={16} />
                {timerActive ? formatTime(timerSeconds) : "Start Timer"}
              </button>
            </div>
            <Card className="p-4 space-y-3">
              {lesson.practice_steps.map((step, idx) => (
                <label
                  key={idx}
                  className="flex items-start gap-3 cursor-pointer group"
                >
                  <div className="pt-0.5">
                    <input
                      type="checkbox"
                      checked={!!completedSteps[idx]}
                      onChange={(e) => setCompletedSteps(prev => ({ ...prev, [idx]: e.target.checked }))}
                      className="sr-only"
                      data-testid={`step-${idx}`}
                    />
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors
                      ${completedSteps[idx] 
                        ? "bg-[#4A6741] border-[#4A6741]" 
                        : "border-[#E6E2DD] group-hover:border-[#4A6741]"}`}
                    >
                      {completedSteps[idx] && <Check size={14} className="text-white" />}
                    </div>
                  </div>
                  <span className={`text-[#292524] transition-colors ${completedSteps[idx] ? "line-through opacity-60" : ""}`}>
                    {step}
                  </span>
                </label>
              ))}
            </Card>
          </section>

          {/* 4. Reflection */}
          <section className="space-y-4 animate-fade-in-up stagger-3">
            <p className="text-xs uppercase tracking-wide text-[#78716C]">Reflection</p>
            <div className="space-y-4">
              {lesson.reflection_prompts.map((prompt, idx) => (
                <div key={idx} className="space-y-2">
                  <p className="font-medium text-[#292524]">{prompt}</p>
                  <Textarea
                    value={reflections[prompt] || ""}
                    onChange={(e) => handleReflectionChange(prompt, e.target.value)}
                    onBlur={() => handleReflectionBlur(prompt)}
                    placeholder="Write your reflection..."
                    rows={4}
                    data-testid={`reflection-${idx}`}
                  />
                </div>
              ))}
            </div>
          </section>

          {/* 5. Completion */}
          <section className="space-y-4 animate-fade-in-up stagger-4">
            <p className="text-xs uppercase tracking-wide text-[#78716C]">Completion</p>
            <Card className="p-5 space-y-4">
              <div className="space-y-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center
                    ${allStepsCompleted ? "bg-[#4A6741] border-[#4A6741]" : "border-[#E6E2DD]"}`}>
                    {allStepsCompleted && <Check size={12} className="text-white" />}
                  </div>
                  <span className="text-[#292524]">Practice steps completed</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center
                    ${allReflectionsFilled ? "bg-[#4A6741] border-[#4A6741]" : "border-[#E6E2DD]"}`}>
                    {allReflectionsFilled && <Check size={12} className="text-white" />}
                  </div>
                  <span className="text-[#292524]">Reflections written</span>
                </label>
              </div>
              <Button 
                onClick={handleComplete} 
                disabled={completing}
                className="w-full"
                data-testid="complete-lesson-btn"
              >
                {completing ? "Completing..." : "Complete Lesson"}
              </Button>
            </Card>
          </section>
        </div>

        {/* Completion Glow Effect */}
        <FullScreenGlow show={showCompletionGlow} />

        {/* Share Modal with Completion Glow */}
        {showShare && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50">
            {/* Golden celebration glow */}
            <div 
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
              style={{
                background: 'radial-gradient(circle at center, rgba(184, 149, 108, 0.15) 0%, transparent 50%)'
              }}
            />
            
            <Card className="max-w-sm w-full p-6 space-y-4 animate-fade-in-up relative z-10 card-completion-glow">
              <div className="text-center space-y-2">
                <div className="w-16 h-16 bg-[#4A6741]/10 rounded-full flex items-center justify-center mx-auto radial-glow">
                  <Check className="text-[#4A6741] check-glow" size={32} />
                </div>
                <h3 className="font-heading text-2xl text-[#292524]">Well done!</h3>
                <p className="text-[#78716C]">You've completed this lesson</p>
              </div>
              
              <div className="bg-[#F0EFEB] rounded-xl p-4">
                <p className="text-[#292524] italic">"{shareText}"</p>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={copyToClipboard}
                  className="flex-1"
                  data-testid="copy-share-btn"
                >
                  <Copy size={18} /> Copy
                </Button>
                <Button
                  onClick={() => {
                    setShowShare(false);
                    navigate(-1);
                  }}
                  className="flex-1"
                  data-testid="done-btn"
                >
                  Done
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </PageWrapper>
  );
};
